import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, type AdminOverview } from '../../lib/api';

export function OverviewPage() {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const data = await adminApi.getOverview();
        setOverview(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load overview');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOverview();
    // Refresh every 30 seconds
    const interval = setInterval(fetchOverview, 30000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="text-mist animate-pulse">Loading system overview...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-ember">{error}</div>
      </div>
    );
  }

  if (!overview) return null;

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl text-parchment">System Overview</h1>
        <div className="flex items-center gap-4 text-sm text-mist">
          <span className="px-2 py-1 bg-stone/30 rounded">{overview.environment}</span>
          <span>v{overview.version}</span>
        </div>
      </div>

      {/* Alerts */}
      {overview.alerts.length > 0 && (
        <div className="space-y-2">
          {overview.alerts.map((alert, i) => (
            <div
              key={i}
              className={`p-4 rounded-lg border ${
                alert.type === 'error'
                  ? 'bg-ember/10 border-ember/30 text-ember'
                  : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-500'
              }`}
            >
              <span className="font-medium">{alert.type === 'error' ? 'Error' : 'Warning'}:</span>{' '}
              {alert.message}
            </div>
          ))}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Users" value={overview.stats.totalUsers} />
        <StatCard label="Total Worlds" value={overview.stats.totalWorlds} />
        <StatCard label="Active Worlds (7d)" value={overview.stats.activeWorlds} />
        <StatCard label="Uptime" value={formatUptime(overview.uptime)} />
      </div>

      {/* Activity Stats */}
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          label="Inbound (24h)"
          value={overview.stats.lettersInLast24h}
          subtitle={
            overview.lastActivity.lastInboundAt
              ? `Last: ${formatDate(overview.lastActivity.lastInboundAt)}`
              : 'No activity'
          }
        />
        <StatCard
          label="Outbound (24h)"
          value={overview.stats.lettersOutLast24h}
          subtitle={
            overview.lastActivity.lastOutboundAt
              ? `Last: ${formatDate(overview.lastActivity.lastOutboundAt)}`
              : 'No activity'
          }
        />
      </div>

      {/* Queue Status */}
      <div className="bg-night border border-stone/30 rounded-xl p-6">
        <h2 className="font-display text-lg text-parchment mb-4">Queue Status</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-mist text-left border-b border-stone/30">
                <th className="pb-2 font-medium">Queue</th>
                <th className="pb-2 font-medium text-center">Active</th>
                <th className="pb-2 font-medium text-center">Waiting</th>
                <th className="pb-2 font-medium text-center">Delayed</th>
                <th className="pb-2 font-medium text-center">Failed</th>
                <th className="pb-2 font-medium text-center">Paused</th>
                <th className="pb-2 font-medium text-center">Stalled</th>
                <th className="pb-2 font-medium text-center">Completed</th>
              </tr>
            </thead>
            <tbody>
              {overview.queues.map((queue) => (
                <tr key={queue.name} className="border-b border-stone/20 last:border-0">
                  <td className="py-3 text-parchment font-mono">{queue.name}</td>
                  <td className="py-3 text-center">
                    <span className={queue.active > 0 ? 'text-rune' : 'text-mist/50'}>
                      {queue.active}
                    </span>
                  </td>
                  <td className="py-3 text-center text-mist">{queue.waiting}</td>
                  <td className="py-3 text-center text-mist">{queue.delayed}</td>
                  <td className="py-3 text-center">
                    <span className={queue.failed > 0 ? 'text-ember' : 'text-mist/50'}>
                      {queue.failed}
                    </span>
                  </td>
                  <td className="py-3 text-center">
                    <span className={queue.paused > 0 ? 'text-mist' : 'text-mist/50'}>
                      {queue.paused}
                    </span>
                  </td>
                  <td className="py-3 text-center">
                    <span className={queue.stalled > 0 ? 'text-rose-400' : 'text-mist/50'}>
                      {queue.stalled}
                    </span>
                  </td>
                  <td className="py-3 text-center text-mist/50">{queue.completed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Letters */}
      <div className="bg-night border border-stone/30 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg text-parchment">Recent Letters</h2>
          <div className="text-xs text-mist">Last {overview.recentLetters.length}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-mist text-left border-b border-stone/30">
                <th className="pb-2 font-medium">Time</th>
                <th className="pb-2 font-medium">Direction</th>
                <th className="pb-2 font-medium">Subject</th>
                <th className="pb-2 font-medium">World</th>
                <th className="pb-2 font-medium">From</th>
                <th className="pb-2 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {overview.recentLetters.map((letter) => (
                <tr key={letter.id} className="border-b border-stone/20 last:border-0">
                  <td className="py-3 text-mist text-xs whitespace-nowrap">
                    {formatDate(letter.createdAt)}
                  </td>
                  <td className="py-3">
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${
                        letter.direction === 'in'
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-rune/20 text-rune'
                      }`}
                    >
                      {letter.direction.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 text-parchment truncate max-w-xs">
                    {letter.subject ?? <span className="text-mist/50 italic">No subject</span>}
                  </td>
                  <td className="py-3">
                    <Link
                      to={`/admin/worlds/${letter.worldId}`}
                      className="text-rune hover:underline text-xs font-mono"
                    >
                      {letter.worldAddress.split('@')[0]}
                    </Link>
                    {letter.isQa && (
                      <span className="ml-2 px-2 py-0.5 rounded text-[10px] bg-ember/20 text-ember">
                        QA
                      </span>
                    )}
                  </td>
                  <td className="py-3 text-mist">
                    {letter.fromEmail ?? <span className="text-mist/40">-</span>}
                  </td>
                  <td className="py-3 text-right">
                    <Link
                      to={
                        letter.direction === 'in'
                          ? `/admin/letters-in/${letter.id}`
                          : `/admin/letters-out/${letter.id}`
                      }
                      className="text-rune hover:underline text-xs"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
              {overview.recentLetters.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-mist">
                    No recent letters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Server Info */}
      <div className="bg-night border border-stone/30 rounded-xl p-6">
        <h2 className="font-display text-lg text-parchment mb-4">Server Info</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-mist">Server Time (UTC):</span>
            <span className="ml-2 text-parchment font-mono">{overview.serverTime.utc}</span>
          </div>
          <div>
            <span className="text-mist">Timezone:</span>
            <span className="ml-2 text-parchment">{overview.serverTime.timezone}</span>
          </div>
          {overview.gitCommit && (
            <div>
              <span className="text-mist">Git Commit:</span>
              <span className="ml-2 text-parchment font-mono">
                {overview.gitCommit.slice(0, 8)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  subtitle,
}: {
  label: string;
  value: string | number;
  subtitle?: string;
}) {
  return (
    <div className="bg-night border border-stone/30 rounded-xl p-4">
      <div className="text-mist text-sm mb-1">{label}</div>
      <div className="text-parchment text-2xl font-display">{value}</div>
      {subtitle && <div className="text-mist/50 text-xs mt-1">{subtitle}</div>}
    </div>
  );
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString();
}
